import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowUp, FileText, Plus, Trash2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const inputCls = "mt-1 bg-[#F7F5F0] border-[#DCD8D0]";

const CONDICIONES = [
  ["insurance_type", "Tipo de seguro", "text"],
  ["rc_voluntary", "Responsabilidad civil voluntaria", "text"],
  ["fuel_policy", "Política de combustible", "text"],
  ["fuel_penalty_eur", "Recargo si falta combustible (€)", "number"],
  ["countries_allowed", "Países autorizados", "text"],
  ["max_occupants", "Plazas si el vehículo no las indica", "number"],
];

// Cláusulas del contrato: texto con marcadores {{...}} y **negrita**. Lo que se
// guarda vale para los contratos que se firmen a partir de ahora; los ya
// firmados conservan su copia.
export default function ContractSettings() {
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/contract-terms").then(({ data }) => setT(data)).catch((e) => toast.error(formatError(e)));
  }, []);

  const setClause = (i, k) => (e) => setT((d) => ({ ...d, clauses: d.clauses.map((c, j) => (j === i ? { ...c, [k]: e.target.value } : c)) }));
  const move = (i, delta) => setT((d) => {
    const cl = [...d.clauses];
    const j = i + delta;
    if (j < 0 || j >= cl.length) return d;
    [cl[i], cl[j]] = [cl[j], cl[i]];
    return { ...d, clauses: cl };
  });
  const remove = (i) => setT((d) => ({ ...d, clauses: d.clauses.filter((_, j) => j !== i) }));
  const add = () => setT((d) => ({ ...d, clauses: [...d.clauses, { title: "Nueva cláusula", body: "" }] }));
  const setCond = (k, type) => (e) => setT((d) => ({ ...d, conditions: { ...d.conditions, [k]: type === "number" ? Number(e.target.value) : e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/contract-terms", { clauses: t.clauses, conditions: t.conditions });
      setT(data);
      toast.success("Contrato guardado");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    try {
      const { data } = await api.post("/admin/contract-terms/preview", null, { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank", "noopener");
    } catch (err) {
      toast.error("No se pudo generar la vista previa");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca"><ArrowLeft className="h-4 w-4 mr-2" /> Panel</Link>
        </div>
      </header>
      <main className="container-x py-8 max-w-4xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A]">Contrato</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={preview} data-testid="ct-preview"><FileText className="h-4 w-4 mr-2" /> Vista previa</Button>
            <Button onClick={save} disabled={saving || !t} className="bg-marca hover:bg-marca-hover text-white" data-testid="ct-save">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </div>
        </div>
        <p className="text-sm text-[#5C5C5C]">
          Los cambios valen para los contratos que se firmen desde ahora; los ya firmados no cambian. Antes de usarlo con
          clientes, haz que lo revise un abogado.
        </p>
        {t && (
          <>
            <section className="bg-white border border-[#DCD8D0] rounded-xl p-6 text-sm text-[#5C5C5C]" data-testid="ct-markers">
              <h2 className="font-display text-lg font-semibold text-[#1A1A1A] mb-2">Marcadores</h2>
              <p className="mb-2">Escribe <code>{"{{marcador}}"}</code> y se sustituye por el dato. <code>**texto**</code> sale en negrita; una línea en blanco separa párrafos.</p>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
                {Object.entries(t.markers || {}).map(([k, v]) => (
                  <div key={k}><code className="text-marca">{`{{${k}}}`}</code> — {v}</div>
                ))}
              </div>
            </section>
            <section className="bg-white border border-[#DCD8D0] rounded-xl p-6 grid sm:grid-cols-2 gap-4" data-testid="ct-conditions">
              <h2 className="sm:col-span-2 font-display text-lg font-semibold">Condiciones de la portada</h2>
              {CONDICIONES.map(([k, label, type]) => (
                <div key={k}>
                  <Label htmlFor={`ct-${k}`}>{label}</Label>
                  <Input id={`ct-${k}`} type={type} className={inputCls} value={t.conditions[k]} onChange={setCond(k, type)} />
                </div>
              ))}
            </section>
            {t.clauses.map((c, i) => (
              <section key={i} className="bg-white border border-[#DCD8D0] rounded-xl p-6 space-y-3" data-testid={`ct-clause-${i}`}>
                <div className="flex items-center gap-2">
                  <span className="font-display font-semibold text-[#8A8A8A] w-8">{i + 1}.</span>
                  <Input className="bg-[#F7F5F0] border-[#DCD8D0] font-semibold" value={c.title} onChange={setClause(i, "title")} data-testid={`ct-title-${i}`} />
                  <Button variant="ghost" size="icon" onClick={() => move(i, -1)} aria-label="Subir"><ArrowUp className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => move(i, 1)} aria-label="Bajar"><ArrowDown className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(i)} aria-label="Quitar"><Trash2 className="h-4 w-4" /></Button>
                </div>
                <Textarea className="bg-[#F7F5F0] border-[#DCD8D0] min-h-[140px]" value={c.body} onChange={setClause(i, "body")} data-testid={`ct-body-${i}`} />
              </section>
            ))}
            <Button variant="outline" onClick={add} data-testid="ct-add"><Plus className="h-4 w-4 mr-2" /> Añadir cláusula</Button>
          </>
        )}
      </main>
    </div>
  );
}
